/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { useBuilderStore } from '@/stores/builder';
import React from 'react';
import WrapperPreview, { WrapperTheme } from '@/components/sections/builder/wrapper';
import { shallow } from 'zustand/shallow';

type BuilderPreviewPageProps = {
  slug?: string;
  groupHeader?: string;
  groupBody?: string;
  groupFooter?: string;
};

export default function BuilderPreviewPage({
  groupHeader = 'header',
  groupBody = 'body',
  groupFooter = 'footer',
}: BuilderPreviewPageProps) {
  const builder = useBuilderStore(
    (state) => ({
      lastUpdatedAt: state.lastUpdatedAt,
      theme: state.theme,
      elements: state.elements,
      selectedId: state.selectedId,
      selectedParentId: state.selectedParentId,
      draggingId: state.draggingId,
      isChildDragging: state.isChildDragging,
      setDefault: state.setDefault,
    }),
    shallow
  );

  React.useEffect(() => {
    window?.top?.postMessage({
      type: 'builder',
      elements: builder.elements,
      theme: builder.theme,
      selectedId: builder.selectedId,
      draggingId: builder.draggingId,
    });
  }, [builder.lastUpdatedAt]);

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'builder') {
        builder.setDefault(
          event.data?.elements,
          event.data?.theme,
          event.data?.selectedId,
          event.data?.draggingId,
          event.data?.selectedParentId
        );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const scrollToId = (id: string) => {
    const view = document.getElementById(
      `${builder.isChildDragging() ? 'view-child' : 'view'}-${id}`
    );
    if (view) {
      const headerOffset = 256;
      const elementPosition = view.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  React.useEffect(() => {
    if (builder.selectedId) {
      scrollToId(builder.selectedId);
    }
  }, [builder.selectedId]);

  React.useEffect(() => {
    if (builder.selectedParentId) {
      scrollToId(builder.selectedParentId);
    }
  }, [builder.selectedParentId]);

  React.useEffect(() => {
    if (builder.draggingId) {
      scrollToId(builder.draggingId);
    }
  }, [builder.draggingId]);

  const getGroupElements = React.useMemo(() => {
    return {
      header: groupHeader,
      body: groupBody,
      footer: groupFooter,
    };
  }, [groupHeader, groupBody, groupFooter]);

  return (
    <WrapperTheme theme={builder.theme}>
      <WrapperPreview
        group={getGroupElements.header}
        title='Header'
        minHeight={86}
        classNameWrapper='after:bg-[#F1AE70] border-[#F1AE70]'
      />
      <WrapperPreview
        group={getGroupElements.body}
        title='Body'
        minHeight={520}
        classNameWrapper='after:bg-primary border-primary'
      />
      <WrapperPreview
        group={getGroupElements.footer}
        title='Footer'
        minHeight={172}
        classNameWrapper='after:bg-[#F1AE70] border-[#F1AE70]'
      />
    </WrapperTheme>
  );
}
